import React, { useState, memo } from 'react';
import { NodeViewWrapper, NodeViewContent } from '@tiptap/react';
import { CodeBlockCopyButton } from '../../../../coding/shared/CodeBlockCopyButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'classnames';
import { twMerge } from 'tailwind-merge';
import { fullLanguageMap } from '@scale/shared/sphereEngine/types';
import { Node as PMNode } from '@tiptap/pm/model';
import { Editor as TipTapEditor } from '@tiptap/core';
import { CodeBlockPreviewNodeT } from './CodeBlockPreview';

// Function to convert language identifier to a readable format
const getReadableLanguage = (language: string) => {
  return fullLanguageMap[language] || language.toUpperCase();
};

const NUM_LINES_COLLAPSED = 15;

export default memo(function CodeBlockPreviewNodeView({
  node: proseMirrorNode,
  extension,
}: {
  selected: boolean;
  editor: TipTapEditor;
  node: PMNode;
  extension: CodeBlockPreviewNodeT;
  getPos: () => number;
  updateAttributes: (attrs: Record<string, any>) => void;
}) {
  const code: string = (proseMirrorNode as any)?.content?.content?.[0]?.text ?? '';
  const languageOverride: string = proseMirrorNode?.attrs?.language ?? 'plaintext';

  const [isExpanded, setIsExpanded] = useState(extension.options.defaultExpanded);
  const language = languageOverride ?? '';

  const toggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const numLines = code.split(/\r\n|\r|\n/).length;

  return (
    <NodeViewWrapper className="hljs rounded-md">
      <>
        <div
          className={twMerge(
            'group bg-transparent relative px-0 py-0 scale-theme',
            isExpanded ? 'overflow-auto' : 'overflow-hidden',
          )}
        >
          <div className="text-white text-xs font-medium font-mono px-3 flex items-center justify-between bg-neutral-800 rounded-t-md py-1.5">
            <span className="text-neutral-0">{getReadableLanguage(language)}</span>
            <div className="flex items-center justify-end">
              <div className="pl-2 pr-3 pt-1">
                <CodeBlockCopyButton code={code} />
              </div>
              {numLines > NUM_LINES_COLLAPSED && isExpanded && (
                <button
                  onClick={e => toggleExpand(e)}
                  className="border ml-1 rounded-md py-1.5 px-2 text-xs flex items-center justify-center bg-neutral-600 border-neutral-500 text-neutral-0"
                >
                  Collapse
                  <FontAwesomeIcon icon="chevron-up" className="ml-1" />
                </button>
              )}
            </div>
          </div>
          <div>
            <div
              className={cn(
                'p-3 bg-neutral-900',
                isExpanded
                  ? 'max-h-[900px] overflow-auto rounded-b-md'
                  : 'max-h-[290px] overflow-hidden',
              )}
            >
              <NodeViewContent className="not-wysiwyg font-mono" />
            </div>
            {numLines > NUM_LINES_COLLAPSED && !isExpanded && (
              <div className="flex font-mono items-center justify-center py-2 text-white text-xs font-medium bg-neutral-900 rounded-b-md">
                <button
                  onClick={e => toggleExpand(e)}
                  className="border ml-1 text-white rounded-md py-1.5 px-2 text-xs flex items-center justify-center bg-neutral-600 border-neutral-500 text-neutral-0"
                >
                  Expand
                  <FontAwesomeIcon icon="chevron-down" className="ml-1" />
                </button>
              </div>
            )}
          </div>
        </div>
      </>
    </NodeViewWrapper>
  );
});
